// ItineraryStrip — visual summary of a multi-property enquiry.
// Shown when ≥1 property is in the basket. Visualises each stop with
// nights, total guests and total nights so guests can sanity-check the
// trip at a glance before sending the inquiry.

import { HOTELS } from "@/lib/data";
import { isBivouac } from "@/lib/propertyType";
import PropertyThumb from "@/components/PropertyThumb";

const pick = (obj, lang) => (obj?.[lang] ?? obj?.en ?? "");

const COPY = {
  overline:    { en: "Itinerary",       fr: "Itinéraire",       es: "Itinerario" },
  stops:       { en: "stops",           fr: "étapes",           es: "etapas" },
  stop:        { en: "stop",            fr: "étape",            es: "etapa" },
  nights:      { en: "nights total",    fr: "nuits au total",   es: "noches en total" },
  night:       { en: "night total",     fr: "nuit au total",    es: "noche en total" },
  guests:      { en: "guests",          fr: "voyageurs",        es: "huéspedes" },
  pending:     { en: "dates pending",   fr: "dates à confirmer", es: "fechas pendientes" },
  est_night:   { en: "night",           fr: "nuit",             es: "noche" },
  est_nights:  { en: "nights",          fr: "nuits",            es: "noches" },
  hotel_label: { en: "Hotel",           fr: "Hôtel",            es: "Hotel" },
  bivouac_label: { en: "Bivouac",       fr: "Bivouac",          es: "Vivac" },
};

const nightsFor = (b) => {
  if (!b.check_in || !b.check_out) return null;
  const n = Math.round((new Date(b.check_out) - new Date(b.check_in)) / 86400000);
  return n > 0 ? n : null;
};

const localeMap = { en: "en-GB", fr: "fr-FR", es: "es-ES" };
const fmtShort = (iso, lang) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(localeMap[lang] || "en-GB", { day: "2-digit", month: "short" });
  } catch {
    return null;
  }
};

export default function ItineraryStrip({ blocks, lang }) {
  if (!blocks || blocks.length === 0) return null;

  let totalNights = 0;
  let totalGuests = 0;
  let anyDates = false;
  blocks.forEach((b) => {
    const n = nightsFor(b);
    if (n != null) { totalNights += n; anyDates = true; }
    (b.rooms || []).forEach((r) => {
      totalGuests += (Number(r.adults) || 0) + (Number(r.children) || 0) + (Number(r.infants) || 0);
    });
  });

  return (
    <div
      className="border border-[#D4A373]/40 bg-[#F2EBE1]/40 px-4 md:px-5 py-4"
      data-testid="home-booking-itinerary"
    >
      {/* Header row: totals */}
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#A07042]">
          {pick(COPY.overline, lang)}
        </p>
        <p className="text-[11px] text-[#5C5248]">
          <span className="text-[#2C2621] font-medium">{blocks.length}</span>{" "}
          {blocks.length === 1 ? pick(COPY.stop, lang) : pick(COPY.stops, lang)}
          <span className="text-[#D4A373] mx-2">·</span>
          {anyDates ? (
            <>
              <span className="text-[#2C2621] font-medium">{totalNights}</span>{" "}
              {totalNights === 1 ? pick(COPY.night, lang) : pick(COPY.nights, lang)}
            </>
          ) : (
            <em className="text-[#5C5248]/70">{pick(COPY.pending, lang)}</em>
          )}
          <span className="text-[#D4A373] mx-2">·</span>
          <span className="text-[#2C2621] font-medium">{totalGuests}</span>{" "}
          {pick(COPY.guests, lang)}
        </p>
      </div>

      {/* Stops rail — horizontal scroll on overflow */}
      <ol
        className="flex items-stretch gap-0 overflow-x-auto pb-1 [scrollbar-width:thin]"
        data-testid="home-booking-itinerary-rail"
      >
        {blocks.map((b, idx) => {
          const hotel = HOTELS.find((h) => h.slug === b.slug);
          const nights = nightsFor(b);
          const isLast = idx === blocks.length - 1;
          const checkIn = fmtShort(b.check_in, lang);
          const checkOut = fmtShort(b.check_out, lang);
          return (
            <li
              key={b.rowId}
              className="flex items-stretch shrink-0"
              data-testid={`home-booking-itinerary-stop-${b.rowId}`}
            >
              <div className="flex items-start gap-3 min-w-[14rem] max-w-[20rem] pr-3">
                <PropertyThumb
                  slug={b.slug}
                  alt={hotel?.name || b.slug}
                  aspectRatio="4/3"
                  className="w-24 h-[72px] sm:w-28 sm:h-20 shrink-0"
                  testId={`home-booking-itinerary-${b.rowId}-thumb`}
                  cmsSlot={`booking-widget.thumb.${b.slug}`}
                />
                <div className="flex flex-col items-start gap-1 min-w-0">
                  {/* Stop dot + index */}
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#A07042]">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-[#C16542] text-[#C16542] text-[10px] tabular-nums font-medium">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    {isBivouac(b.slug)
                      ? pick(COPY.bivouac_label, lang)
                      : pick(COPY.hotel_label, lang)}
                  </div>
                  <p className="text-sm text-[#2C2621] leading-snug font-serif-x break-words w-full" title={hotel?.name}>
                    {hotel?.name || b.slug}
                  </p>
                  <p className="text-[11px] text-[#5C5248] tabular-nums">
                    {checkIn && checkOut ? (
                      <>
                        {checkIn} <span className="text-[#D4A373]">→</span> {checkOut}
                      </>
                    ) : (
                      <em className="text-[#5C5248]/70">{pick(COPY.pending, lang)}</em>
                    )}
                  </p>
                  <p className="text-[11px] text-[#C16542]">
                    {nights ? (
                      <>
                        {nights} {nights === 1 ? pick(COPY.est_night, lang) : pick(COPY.est_nights, lang)}
                      </>
                    ) : (
                      "—"
                    )}
                  </p>
                </div>
              </div>
              {/* Connector — gold dashed line between stops */}
              {!isLast ? (
                <div className="flex items-center pr-3">
                  <span className="w-8 sm:w-10 border-t border-dashed border-[#D4A373]/60" aria-hidden="true" />
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
